import axios from "axios";
import { useState, useEffect } from "react";
import {
  useNavigate,
  Link as RouterLink,
  useSearchParams,
} from "react-router-dom";
import { Box, Typography, Link } from "@mui/material";
import useApi from "../../hooks/useApi";
import { useSnackbar } from "notistack";
import SessionService from "../../services/SessionService";
import { useCerberus } from "../../hooks/useCerberus";
import Form from "../Forms/Form";
import { FormField } from "../Forms/FormField";
import { FieldTypes } from "../Forms/FieldTypes";
import * as Yup from "yup";
import { getPostLoginRedirectUrl } from "../../utils/redirectUtils";
import { useBranding } from "../../branding/BrandingProvider";
import AuthPageFrame, { AUTH_LINK_COLOR } from "./AuthPageFrame";
import { isModuleEnabled } from "../../modules/activeModules";
import { isSignupBlocked } from "../../config/runtimeConfig";

const Login = () => {
  const brand = useBranding();
  const linkColor = AUTH_LINK_COLOR;
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSession, setMfaSession] = useState("");
  const [mfaDestination, setMfaDestination] = useState("");
  // A patient whose code was texted may ask for it by email instead (Derek, 2026-10-21). The
  // server decides who may: staff and providers must use their phone.
  const [mfaEmailFallback, setMfaEmailFallback] = useState(false);
  const [username, setUsername] = useState("");
  interface MfaError {
    requiresMfa?: boolean;
    mfaSession?: string | null;
    message?: string;
  }
  const [mfaError, setMfaError] = useState<MfaError | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const api = useApi(false);
  const { enqueueSnackbar } = useSnackbar();
  const sessionService = SessionService.getInstance();
  const { refreshUserData } = useCerberus();

  // Handle MFA state updates
  useEffect(() => {
    if (mfaError) {
      // Special case for attempt limit exceeded
      if (mfaError.message?.includes("Attempt limit exceeded")) {
        setMfaRequired(false);
        setMfaSession("");
        setUsername("");
        enqueueSnackbar(mfaError.message, { variant: "error" });
      } else if (mfaError.requiresMfa) {
        setMfaRequired(true);
        if (mfaError.mfaSession) {
          setMfaSession(mfaError.mfaSession);
        }
        enqueueSnackbar(mfaError.message || "Verification failed", {
          variant: "error",
        });
      } else {
        setMfaRequired(false);
        setMfaSession("");
        setUsername("");
        enqueueSnackbar(mfaError.message || "Please log in again", {
          variant: "error",
        });
      }
      setMfaError(null);
    }
  }, [mfaError, enqueueSnackbar]);

  const handleLogin = async (values: Record<string, unknown>) => {
    setLoading(true);

    try {
      const response = await api.post<{
        success: boolean;
        requiresMfa?: boolean;
        mfaSession?: string;
        mfaChannel?: string;
        mfaDestination?: string;
        mfaEmailFallback?: boolean;
        expiresIn?: number;
        message?: string;
      }>(
        "/auth/login",
        {
          username: values.email,
          password: values.password,
        },
        // A 401 here is the server answering "invalid username or password". Left to the shared
        // client it was read as an expired session, and the reload of "/" wiped the message this
        // screen raises below before anyone could read it (NEW-31).
        //
        // `suppressErrorDialog` comes with it, under BUG-7's rule that exactly one thing reports:
        // the reload used to hide the app-wide dialog as a side effect, so not suppressing it now
        // would trade a lost message for two. The catch below reports every failure here, not just
        // the 401 — the same claim the MFA and resend calls on this screen already make.
        { credentialAttempt: true, suppressErrorDialog: true },
      );

      if (response.data.success) {
        if (response.data.requiresMfa) {
          // Store MFA session and show MFA input
          setMfaRequired(true);
          setMfaSession(response.data.mfaSession ?? "");
          setMfaDestination(response.data.mfaDestination ?? "");
          setMfaEmailFallback(!!response.data.mfaEmailFallback);
          setUsername(values.email as string);
          enqueueSnackbar(
            response.data.mfaDestination
              ? `Enter the verification code sent to ${response.data.mfaDestination}`
              : "Please enter the verification code we sent you",
            {
              variant: "info",
            },
          );
        } else {
          // Normal login flow. The server sets the session cookie on this
          // response; we only need to record the expiry locally for countdowns.
          sessionService.setSession(response.data.expiresIn!);
          await refreshUserData();
          const redirectUrl = getPostLoginRedirectUrl(searchParams);
          navigate(redirectUrl);
        }
      } else {
        enqueueSnackbar(response.data.message || "Login failed", {
          variant: "error",
        });
      }
    } catch (error) {
      let message = "Login failed";
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || message;
      }
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (values: Record<string, unknown>) => {
    setLoading(true);

    try {
      const response = await api.post(
        "/auth/verify-mfa",
        {
          username: username,
          code: values.code,
          session: mfaSession,
          rememberDevice: values.rememberDevice === true,
        },
        // A wrong OTP digit is a 400, and this screen already reports it — through `setMfaError`,
        // which keeps the code form on screen and says what to do next. Without this flag the shared
        // client's response interceptor ALSO reported it, so one mistyped digit put a modal on top of
        // the field the user is retyping. BUG-7's rule: exactly one thing reports, and here it is the
        // screen, because the screen has somewhere better to put the message.
        { suppressErrorDialog: true },
      );

      if (import.meta.env.DEV)
        console.log("MFA verification response:", response.data);

      if (response.data.success) {
        sessionService.setSession(response.data.expiresIn);
        await refreshUserData();
        const redirectUrl = getPostLoginRedirectUrl(searchParams);
        navigate(redirectUrl);
      } else {
        // Check for attempt limit exceeded
        if (response.data.message?.includes("Attempt limit exceeded")) {
          setMfaError({
            requiresMfa: false,
            mfaSession: null,
            message: response.data.message,
          });
        } else {
          setMfaError({
            requiresMfa: true, // Keep MFA form visible for other errors
            mfaSession: response.data.mfaSession,
            message: response.data.message || "Verification failed",
          });
        }
      }
    } catch (error) {
      const errorData = axios.isAxiosError(error)
        ? error.response?.data
        : undefined;
      if (import.meta.env.DEV)
        console.log("MFA verification error:", errorData);

      // Check for attempt limit exceeded in error response
      if (errorData?.message?.includes("Attempt limit exceeded")) {
        setMfaError({
          requiresMfa: false,
          mfaSession: null,
          message: errorData.message,
        });
      } else {
        setMfaError({
          requiresMfa: true, // Keep MFA form visible for other errors
          mfaSession: errorData?.mfaSession,
          message: errorData?.message || "Verification failed",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async (byEmail = false) => {
    try {
      const response = await api.post<{
        success: boolean;
        mfaSession?: string;
        mfaDestination?: string;
        mfaEmailFallback?: boolean;
        message?: string;
      }>(
        "/auth/resend-mfa",
        { session: mfaSession, byEmail },
        // Same defect, same fix, one screen over: a spent attempt cap or resend cap is a 400 that
        // this handler already turns into a snackbar saying what to do.
        { suppressErrorDialog: true },
      );

      if (response.data.success) {
        if (response.data.mfaSession) setMfaSession(response.data.mfaSession);
        if (response.data.mfaDestination)
          setMfaDestination(response.data.mfaDestination);
        setMfaEmailFallback(!!response.data.mfaEmailFallback);
        enqueueSnackbar(response.data.message || "A new code has been sent.", {
          variant: "info",
        });
      } else {
        enqueueSnackbar(response.data.message || "Please log in again.", {
          variant: "error",
        });
      }
    } catch (error) {
      let message = "Couldn't resend the code. Please try again.";
      if (axios.isAxiosError(error)) {
        message = error.response?.data?.message || message;
      }
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const loginFields: FormField[] = [
    {
      id: "email",
      email: true,
      text: "Email Address",
      fixedLabel: true,
      type: FieldTypes.Text,
      columns: 12,
      autocomplete: "username",
      yupSchema: Yup.string()
        .email("Invalid email address")
        .required("Email is required")
        .max(254, "Email address is too long"),
    },
    {
      id: "password",
      text: "Password",
      fixedLabel: true,
      type: FieldTypes.Text,
      password: true,
      columns: 12,
      autocomplete: "current-password",
      yupSchema: Yup.string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters"),
    },
  ];

  const mfaFields: FormField[] = [
    {
      id: "code",
      text: "Verification Code",
      fixedLabel: true,
      type: FieldTypes.Text,
      columns: 12,
      // one-time-code lets iOS/macOS autofill the SMS/email code.
      autocomplete: "one-time-code",
      yupSchema: Yup.string()
        .required("Code is required")
        .matches(/^\d+$/, "Code must contain only numbers")
        .length(6, "Code must be 6 digits"),
    },
    {
      id: "rememberDevice",
      text: "Don't ask again on this device for 7 days",
      type: FieldTypes.Checkbox,
      columns: 12,
      value: false,
    },
  ];

  return (
    <AuthPageFrame
      eyebrow={mfaRequired ? "Verification" : "Welcome back"}
      title={mfaRequired ? "Enter your code" : "Sign in to your account"}
      titleGap={mfaRequired ? 2 : 3.5}
    >
      {mfaRequired ? (
        <>
          <Typography
            variant="body2"
            sx={{
              mb: 3,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            {mfaDestination
              ? `Enter the verification code we sent to ${mfaDestination}.`
              : "Enter the verification code we sent you."}
          </Typography>
          <Form
            fields={mfaFields}
            onSubmit={handleMfaSubmit}
            submitButtonText={loading ? "Verifying..." : "Verify Code"}
            fieldVariant="outlined"
            centerActionButtons
          />
          <Box sx={{ mt: 3, textAlign: "center" }}>
            {/*
              NEW-33. This was a `<Typography onClick>`: no role, not focusable, and
              unreachable by keyboard or screen reader — on the one screen a user cannot get
              past without it when the code never arrives. A Link with `component="button"`
              keeps the appearance exactly and gets the semantics for free (button role,
              tab stop, Enter/Space activation) rather than re-deriving them with tabIndex +
              onKeyDown.

              `type="button"` is load-bearing: this sits below the MFA <Form>, and a bare
              <button> defaults to type="submit" — inside a form that would submit the
              half-typed code instead of resending.
            */}
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              onClick={() => handleResendCode()}
              sx={{ color: linkColor, fontWeight: 500 }}
            >
              Didn't get it? Resend code
            </Link>
            {mfaEmailFallback && (
              <Box sx={{ mt: 1.5 }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => handleResendCode(true)}
                  sx={{ color: linkColor, fontWeight: 500 }}
                >
                  Send it by email instead
                </Link>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <>
          <Form
            fields={loginFields}
            onSubmit={handleLogin}
            submitButtonText={loading ? "Signing in..." : "Sign In"}
            fieldVariant="outlined"
            centerActionButtons
          />
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <RouterLink
              to="/forgot-password"
              style={{ textDecoration: "none" }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: linkColor,
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Forgot password?
              </Typography>
            </RouterLink>
            {/* NEW-57(b): the patient whose invitation email never came has no session to
                resend it from, so the way back in has to be reachable from here. */}
            {isModuleEnabled("patient-self-signup") && !isSignupBlocked() && (
              <RouterLink
                to="/i/resend-invitation"
                style={{ textDecoration: "none" }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    color: linkColor,
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Didn't get your invitation email?
                </Typography>
              </RouterLink>
            )}
            {/* TeleHeadache patients sign themselves up; without this the only way to the
                signup page was a marketing-site link. Same gate as the route itself, so
                CerebraLinked (invite-only) never shows it. */}
            {isModuleEnabled("patient-self-signup") && (
              <Box
                sx={{ mt: 3, pt: 2.5, borderTop: 1, borderColor: "divider" }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  New to {brand.displayName}?{" "}
                  <Link
                    component={RouterLink}
                    to="/i/new-patient"
                    sx={{ color: linkColor, fontWeight: 700 }}
                  >
                    {/* Same page either way; before launch it is the waitlist. */}
                    {isSignupBlocked()
                      ? "Get notified when we launch"
                      : "Create an account"}
                  </Link>
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </AuthPageFrame>
  );
};

export default Login;
